import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { scaleIn } from "@/lib/animations";
import {
  formatPublished,
  platformLabel,
  type Video,
} from "@/lib/videos";

type Props = {
  video: Video;
  index?: number;
};

/**
 * A single video tile. Links out to the source platform (new tab). Designed to
 * sit inside a framer-motion `stagger` container — it animates in via `scaleIn`
 * using its `index` for the staggered delay.
 */
export default function VideoCard({ video, index = 0 }: Props) {
  const theme = usePalette();
  const accent = theme.accent;
  const published = formatPublished(video.publishedAt);

  return (
    <motion.a
      href={video.externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      variants={scaleIn}
      custom={index}
      className="group block rounded-2xl overflow-hidden border hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
      style={{ backgroundColor: "white", borderColor: `${colors.tan}25` }}
    >
      <div
        className="relative w-full aspect-video overflow-hidden"
        style={{ backgroundColor: `${accent}12` }}
      >
        {video.thumbnailUrl ? (
          <img
            src={video.thumbnailUrl}
            alt={video.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Play size={32} style={{ color: accent }} />
          </div>
        )}

        {/* play overlay */}
        <div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ backgroundColor: `${colors.navy}33` }}
        >
          <span
            className="flex items-center justify-center w-14 h-14 rounded-full shadow-lg"
            style={{ backgroundColor: colors.cream }}
          >
            <Play size={22} style={{ color: accent }} fill={accent} />
          </span>
        </div>

        {/* platform badge */}
        <span
          className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase"
          style={{
            fontFamily: fonts.body,
            fontWeight: 700,
            backgroundColor: `${colors.cream}e6`,
            color: colors.navy,
          }}
        >
          {platformLabel(video.platform)}
        </span>
      </div>

      <div className="p-5">
        <h3
          className="text-base leading-snug mb-2 line-clamp-2"
          style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}
        >
          {video.title}
        </h3>
        <div
          className="flex items-center gap-3 text-xs"
          style={{ fontFamily: fonts.body, fontWeight: 500, color: `${colors.navy}88` }}
        >
          {/* View count hidden for now */}
          {/* {video.viewCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <Eye size={13} />
              {formatViews(video.viewCount)} views
            </span>
          )} */}
          {published && <span>{published}</span>}
        </div>
      </div>
    </motion.a>
  );
}
